import React, { useState, useRef, useCallback } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { createAgencyAccount } from '../../../services/agencyManagementService';

// Expected CSV columns
const REQUIRED_COLUMNS = ['agencyName', 'email', 'password', 'contactName'];
const OPTIONAL_COLUMNS = ['phone', 'city', 'subscription', 'status'];
const ALL_COLUMNS = [...REQUIRED_COLUMNS, ...OPTIONAL_COLUMNS];

const SAMPLE_CSV = `agencyName,email,password,contactName,phone,city,subscription,status
Alraqia Travels,admin@alraqia-travels.ma,Password@2026!,Mohamed Alami,+212612345678,Casablanca,standard,active
Nour Voyages,admin@nour-voyages.ma,Password@2026!,Fatima Zahra,+212698765432,Rabat,premium,active`;

function parseCSV(text) {
  const lines = text?.trim()?.split(/\r?\n/);
  if (lines?.length < 2) return { headers: [], rows: [], error: 'Le fichier CSV est vide ou ne contient pas de données.' };

  const headers = lines?.[0]?.split(',')?.map(h => h?.trim()?.replace(/^"|"$/g, ''));
  const rows = lines?.slice(1)?.map((line, idx) => {
    const values = line?.split(',')?.map(v => v?.trim()?.replace(/^"|"$/g, ''));
    const row = {};
    headers?.forEach((h, i) => { row[h] = values?.[i] || ''; });
    row._lineNumber = idx + 2;
    return row;
  })?.filter(row => Object.values(row)?.some(v => v && v !== String(row?._lineNumber)));

  return { headers, rows, error: null };
}

function validateRow(row, index) {
  const errors = [];
  if (!row?.agencyName?.trim()) errors?.push('Nom de l\'agence requis');
  if (!row?.email?.trim()) {
    errors?.push('Email requis');
  } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/?.test(row?.email)) {
    errors?.push('Email invalide');
  }
  if (!row?.password?.trim()) {
    errors?.push('Mot de passe requis');
  } else if (row?.password?.length < 8) {
    errors?.push('Mot de passe: 8 caractères minimum');
  }
  if (!row?.contactName?.trim()) errors?.push('Nom du responsable requis');
  return errors;
}

const STATUS_ICON = {
  pending: { icon: 'Clock', color: 'text-muted-foreground', bg: 'bg-muted' },
  success: { icon: 'CheckCircle', color: 'text-success', bg: 'bg-success/10' },
  error: { icon: 'XCircle', color: 'text-destructive', bg: 'bg-destructive/10' },
  skipped: { icon: 'MinusCircle', color: 'text-warning', bg: 'bg-warning/10' },
};

const CSVImportModal = ({ onClose, onSuccess, currentLanguage = 'fr' }) => {
  const [step, setStep] = useState('upload'); // upload | preview | importing | done
  const [dragOver, setDragOver] = useState(false);
  const [csvData, setCsvData] = useState(null);
  const [parseError, setParseError] = useState('');
  const [results, setResults] = useState([]);
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef(null);

  const labels = {
    fr: {
      title: 'Importer des agences via CSV',
      uploadTitle: 'Glissez votre fichier CSV ici',
      uploadSubtitle: 'ou cliquez pour sélectionner un fichier',
      downloadSample: 'Télécharger un exemple CSV',
      columnsRequired: 'Colonnes requises',
      columnsOptional: 'Colonnes optionnelles',
      preview: 'Aperçu des données',
      rowsFound: 'ligne(s) trouvée(s)',
      rowsValid: 'valide(s)',
      rowsInvalid: 'avec erreurs',
      startImport: 'Lancer l\'importation',
      importing: 'Importation en cours...',
      done: 'Importation terminée',
      close: 'Fermer',
      cancel: 'Annuler',
      back: 'Retour',
      success: 'Succès',
      errors: 'Erreurs',
      skipped: 'Ignorées',
      line: 'Ligne',
      status: 'Statut',
      agency: 'Agence',
      email: 'Email',
      details: 'Détails',
      missingColumns: 'Colonnes manquantes dans le CSV',
      invalidFile: 'Fichier invalide. Veuillez utiliser un fichier .csv',
      noValidRows: 'Aucune ligne valide à importer.',
    },
    ar: {
      title: 'استيراد الوكالات عبر CSV',
      uploadTitle: 'اسحب ملف CSV هنا',
      uploadSubtitle: 'أو انقر لاختيار ملف',
      downloadSample: 'تحميل مثال CSV',
      columnsRequired: 'الأعمدة المطلوبة',
      columnsOptional: 'الأعمدة الاختيارية',
      preview: 'معاينة البيانات',
      rowsFound: 'صف(وف) موجود(ة)',
      rowsValid: 'صالح(ة)',
      rowsInvalid: 'بها أخطاء',
      startImport: 'بدء الاستيراد',
      importing: 'جارٍ الاستيراد...',
      done: 'اكتمل الاستيراد',
      close: 'إغلاق',
      cancel: 'إلغاء',
      back: 'رجوع',
      success: 'نجاح',
      errors: 'أخطاء',
      skipped: 'تم تخطيها',
      line: 'السطر',
      status: 'الحالة',
      agency: 'الوكالة',
      email: 'البريد',
      details: 'التفاصيل',
      missingColumns: 'أعمدة مفقودة في CSV',
      invalidFile: 'ملف غير صالح. يرجى استخدام ملف .csv',
      noValidRows: 'لا توجد صفوف صالحة للاستيراد.',
    }
  };

  const l = labels?.[currentLanguage] || labels?.fr;

  const processFile = useCallback((file) => {
    if (!file) return;
    if (!file?.name?.endsWith('.csv') && file?.type !== 'text/csv') {
      setParseError(l?.invalidFile);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e?.target?.result;
      const { headers, rows, error } = parseCSV(text);
      if (error) { setParseError(error); return; }

      // Check required columns
      const missing = REQUIRED_COLUMNS?.filter(col => !headers?.includes(col));
      if (missing?.length > 0) {
        setParseError(`${l?.missingColumns}: ${missing?.join(', ')}`);
        return;
      }

      // Validate each row
      const validatedRows = rows?.map((row, idx) => ({
        ...row,
        _errors: validateRow(row, idx),
        _status: 'pending',
      }));

      setCsvData({ headers, rows: validatedRows });
      setParseError('');
      setStep('preview');
    };
    reader?.readAsText(file);
  }, [l]);

  const handleDrop = useCallback((e) => {
    e?.preventDefault();
    setDragOver(false);
    const file = e?.dataTransfer?.files?.[0];
    processFile(file);
  }, [processFile]);

  const handleFileChange = (e) => {
    processFile(e?.target?.files?.[0]);
  };

  const handleDownloadSample = () => {
    const blob = new Blob([SAMPLE_CSV], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'agencies_sample.csv';
    a?.click();
    URL.revokeObjectURL(url);
  };

  const validRows = csvData?.rows?.filter(r => r?._errors?.length === 0) || [];
  const invalidRows = csvData?.rows?.filter(r => r?._errors?.length > 0) || [];

  const handleImport = async () => {
    if (validRows?.length === 0) return;
    setImporting(true);
    setStep('importing');

    const initialResults = csvData?.rows?.map(row => ({
      lineNumber: row?._lineNumber,
      agencyName: row?.agencyName,
      email: row?.email,
      status: row?._errors?.length > 0 ? 'skipped' : 'pending',
      message: row?._errors?.length > 0 ? row?._errors?.join('; ') : '',
    }));
    setResults(initialResults);

    const updatedResults = [...initialResults];

    for (let i = 0; i < csvData?.rows?.length; i++) {
      const row = csvData?.rows?.[i];
      if (row?._errors?.length > 0) continue; // already marked skipped

      try {
        await createAgencyAccount({
          agencyName: row?.agencyName,
          email: row?.email,
          password: row?.password,
          contactName: row?.contactName,
          phone: row?.phone || '',
          city: row?.city || '',
          subscription: row?.subscription || 'standard',
          status: row?.status || 'active',
        });
        updatedResults[i] = { ...updatedResults?.[i], status: 'success', message: 'Compte créé avec succès' };
      } catch (err) {
        updatedResults[i] = { ...updatedResults?.[i], status: 'error', message: err?.message || 'Erreur inconnue' };
      }

      setResults([...updatedResults]);
      // Small delay to show progress
      await new Promise(res => setTimeout(res, 200));
    }

    setImporting(false);
    setStep('done');
  };

  const successCount = results?.filter(r => r?.status === 'success')?.length;
  const errorCount = results?.filter(r => r?.status === 'error')?.length;
  const skippedCount = results?.filter(r => r?.status === 'skipped')?.length;

  const handleDone = () => {
    if (successCount > 0) onSuccess();
    else onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-background/80 backdrop-blur-sm">
      <div className="bg-card rounded-xl border border-border shadow-warm-md w-full max-w-3xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
              <Icon name="Upload" size={20} className="text-primary" />
            </div>
            <h2 className="text-xl font-semibold text-foreground">{l?.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 rounded-md hover:bg-muted transition-colors" aria-label="Fermer">
            <Icon name="X" size={20} className="text-muted-foreground" />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* STEP: Upload */}
          {step === 'upload' && (
            <div className="space-y-6">
              {/* Drop Zone */}
              <div
                onDragOver={(e) => { e?.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => fileInputRef?.current?.click()}
                className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${
                  dragOver ? 'border-primary bg-primary/5' : 'border-border hover:border-primary/50 hover:bg-muted/30'
                }`}
              >
                <input ref={fileInputRef} type="file" accept=".csv" className="hidden" onChange={handleFileChange} />
                <div className="flex flex-col items-center gap-3">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name="FileSpreadsheet" size={28} className="text-primary" />
                  </div>
                  <div>
                    <p className="font-medium text-foreground">{l?.uploadTitle}</p>
                    <p className="text-sm text-muted-foreground mt-1">{l?.uploadSubtitle}</p>
                  </div>
                  <span className="text-xs text-muted-foreground bg-muted px-3 py-1 rounded-full">CSV</span>
                </div>
              </div>

              {parseError && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-destructive">{parseError}</p>
                </div>
              )}

              {/* Column guide */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">{l?.columnsRequired}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {REQUIRED_COLUMNS?.map(col => (
                      <span key={col} className="text-xs bg-primary/10 text-primary px-2 py-0.5 rounded-full font-mono">{col}</span>
                    ))}
                  </div>
                </div>
                <div className="p-4 rounded-lg bg-muted/50 border border-border">
                  <p className="text-xs font-semibold text-foreground mb-2 uppercase tracking-wide">{l?.columnsOptional}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {OPTIONAL_COLUMNS?.map(col => (
                      <span key={col} className="text-xs bg-muted text-muted-foreground px-2 py-0.5 rounded-full font-mono">{col}</span>
                    ))}
                  </div>
                </div>
              </div>

              <button
                onClick={handleDownloadSample}
                className="flex items-center gap-2 text-sm text-primary hover:underline"
              >
                <Icon name="Download" size={14} />
                {l?.downloadSample}
              </button>
            </div>
          )}

          {/* STEP: Preview */}
          {step === 'preview' && csvData && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex flex-wrap gap-3">
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-muted/50 border border-border">
                  <Icon name="FileText" size={16} className="text-muted-foreground" />
                  <span className="text-sm font-medium text-foreground">{csvData?.rows?.length} {l?.rowsFound}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20">
                  <Icon name="CheckCircle" size={16} className="text-success" />
                  <span className="text-sm font-medium text-success">{validRows?.length} {l?.rowsValid}</span>
                </div>
                {invalidRows?.length > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/20">
                    <Icon name="XCircle" size={16} className="text-destructive" />
                    <span className="text-sm font-medium text-destructive">{invalidRows?.length} {l?.rowsInvalid}</span>
                  </div>
                )}
              </div>

              {/* Table */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.line}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.agency}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.email}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.status}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.details}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {csvData?.rows?.map((row, idx) => {
                        const hasErrors = row?._errors?.length > 0;
                        return (
                          <tr key={idx} className={hasErrors ? 'bg-destructive/5' : 'bg-card'}>
                            <td className="px-3 py-2.5 text-muted-foreground font-mono text-xs">{row?._lineNumber}</td>
                            <td className="px-3 py-2.5 font-medium text-foreground">{row?.agencyName || '—'}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{row?.email || '—'}</td>
                            <td className="px-3 py-2.5">
                              {hasErrors ? (
                                <span className="inline-flex items-center gap-1 text-xs text-destructive">
                                  <Icon name="XCircle" size={12} /> Erreur
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 text-xs text-success">
                                  <Icon name="CheckCircle" size={12} /> Valide
                                </span>
                              )}
                            </td>
                            <td className="px-3 py-2.5 text-xs text-destructive">
                              {hasErrors ? row?._errors?.join(' · ') : ''}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {validRows?.length === 0 && (
                <div className="flex items-center gap-2 p-3 rounded-lg bg-warning/10 border border-warning/20">
                  <Icon name="AlertTriangle" size={16} className="text-warning flex-shrink-0" />
                  <p className="text-sm text-warning">{l?.noValidRows}</p>
                </div>
              )}
            </div>
          )}

          {/* STEP: Importing / Done */}
          {(step === 'importing' || step === 'done') && (
            <div className="space-y-4">
              {/* Summary bar */}
              {step === 'done' && (
                <div className="flex flex-wrap gap-3 mb-2">
                  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-success/10 border border-success/20">
                    <Icon name="CheckCircle" size={16} className="text-success" />
                    <span className="text-sm font-medium text-success">{successCount} {l?.success}</span>
                  </div>
                  {errorCount > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-destructive/10 border border-destructive/20">
                      <Icon name="XCircle" size={16} className="text-destructive" />
                      <span className="text-sm font-medium text-destructive">{errorCount} {l?.errors}</span>
                    </div>
                  )}
                  {skippedCount > 0 && (
                    <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-warning/10 border border-warning/20">
                      <Icon name="MinusCircle" size={16} className="text-warning" />
                      <span className="text-sm font-medium text-warning">{skippedCount} {l?.skipped}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Results table */}
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead className="bg-muted/50">
                      <tr>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.line}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.agency}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.email}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.status}</th>
                        <th className="text-left px-3 py-2.5 text-xs font-semibold text-muted-foreground uppercase tracking-wide">{l?.details}</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                      {results?.map((r, idx) => {
                        const s = STATUS_ICON?.[r?.status] || STATUS_ICON?.pending;
                        return (
                          <tr key={idx} className="bg-card">
                            <td className="px-3 py-2.5 text-muted-foreground font-mono text-xs">{r?.lineNumber}</td>
                            <td className="px-3 py-2.5 font-medium text-foreground">{r?.agencyName || '—'}</td>
                            <td className="px-3 py-2.5 text-muted-foreground">{r?.email || '—'}</td>
                            <td className="px-3 py-2.5">
                              <span className={`inline-flex items-center gap-1 text-xs ${s?.color}`}>
                                {r?.status === 'pending' ? (
                                  <div className="w-3 h-3 border border-current border-t-transparent rounded-full animate-spin" />
                                ) : (
                                  <Icon name={s?.icon} size={12} />
                                )}
                                {r?.status === 'pending' ? '...' : r?.status}
                              </span>
                            </td>
                            <td className="px-3 py-2.5 text-xs text-muted-foreground">{r?.message}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between p-6 border-t border-border flex-shrink-0">
          <div>
            {step === 'preview' && (
              <Button variant="ghost" iconName="ArrowLeft" iconPosition="left" onClick={() => { setStep('upload'); setCsvData(null); }}>
                {l?.back}
              </Button>
            )}
          </div>
          <div className="flex gap-3">
            {step !== 'done' && !importing && (
              <Button variant="outline" onClick={onClose}>{l?.cancel}</Button>
            )}
            {step === 'preview' && (
              <Button
                variant="default"
                iconName="Upload"
                iconPosition="left"
                onClick={handleImport}
                disabled={validRows?.length === 0}
              >
                {l?.startImport} ({validRows?.length})
              </Button>
            )}
            {step === 'importing' && (
              <Button variant="default" disabled>
                <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin mr-2" />
                {l?.importing}
              </Button>
            )}
            {step === 'done' && (
              <Button variant="default" iconName="Check" iconPosition="left" onClick={handleDone}>
                {l?.close}
              </Button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CSVImportModal;
